import jsPDF from 'jspdf';
import { Businesscase, BusinesscaseRevenue, BusinesscaseExtraCost, CalculationResults } from '@/types/businesscase';
import { beoordeelBusinesscase } from './businesscaseOordeel';

interface ProjectInfo {
  name: string;
  project_number?: string | null;
}

interface BusinesscasePDFParams {
  businesscase: Businesscase;
  project: ProjectInfo | null;
  revenues: BusinesscaseRevenue[];
  extraCosts: BusinesscaseExtraCost[];
  calculations: CalculationResults;
  roiMode: 'bruto' | 'netto';
}

// Colors
const GREEN = [34, 197, 94] as const;    // #22c55e
const DARK = [20, 20, 20] as const;
const GRAY = [100, 100, 100] as const;
const LIGHT_GRAY = [245, 245, 245] as const;
const WHITE = [255, 255, 255] as const;
const TABLE_HEADER_BG = [34, 197, 94] as const;
const TABLE_HEADER_TEXT = [255, 255, 255] as const;
const TABLE_ALT_ROW = [245, 250, 245] as const;

const PAGE_WIDTH = 210;
const PAGE_HEIGHT = 297;
const MARGIN = 20;
const CONTENT_WIDTH = PAGE_WIDTH - 2 * MARGIN;

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const formatPercentage = (value: number | null | undefined): string => {
  // Een niet-berekend rendement wordt in de PDF een streepje, geen 0,0%.
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return '-';
  }
  return `${value.toFixed(1)}%`;
};

const formatDate = (): string => {
  return new Date().toLocaleDateString('nl-NL', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

let totalPages = 0;

function addHeader(doc: jsPDF, title: string) {
  // Green header bar
  doc.setFillColor(...GREEN);
  doc.rect(0, 0, PAGE_WIDTH, 18, 'F');
  
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...WHITE);
  doc.text(title, MARGIN, 12);
  
  // GreenVision right side
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text('GreenVision', PAGE_WIDTH - MARGIN, 12, { align: 'right' });
}

function addFooter(doc: jsPDF, pageNum: number) {
  doc.setFontSize(8);
  doc.setTextColor(...GRAY);
  doc.setFont('helvetica', 'normal');
  doc.text(`Pagina ${pageNum}`, PAGE_WIDTH - MARGIN, PAGE_HEIGHT - 10, { align: 'right' });
  doc.text(`Gegenereerd op ${formatDate()}`, MARGIN, PAGE_HEIGHT - 10);
}

function drawTable(
  doc: jsPDF,
  startY: number,
  headers: string[],
  rows: string[][],
  colWidths: number[],
  options?: { totalRow?: boolean }
): number {
  const rowHeight = 8;
  const headerHeight = 10;
  let y = startY;

  // Header
  doc.setFillColor(...TABLE_HEADER_BG);
  doc.rect(MARGIN, y, CONTENT_WIDTH, headerHeight, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...TABLE_HEADER_TEXT);
  
  let x = MARGIN + 3;
  headers.forEach((h, i) => {
    if (i === 0) {
      doc.text(h, x, y + 7);
    } else {
      doc.text(h, x + colWidths[i] - 3, y + 7, { align: 'right' });
    }
    x += colWidths[i];
  });
  
  y += headerHeight;

  // Rows
  rows.forEach((row, rowIndex) => {
    const isTotal = options?.totalRow && rowIndex === rows.length - 1;
    const isAlt = rowIndex % 2 === 1;
    
    if (isTotal) {
      doc.setFillColor(...GREEN);
      doc.rect(MARGIN, y, CONTENT_WIDTH, rowHeight, 'F');
      doc.setTextColor(...WHITE);
      doc.setFont('helvetica', 'bold');
    } else if (isAlt) {
      doc.setFillColor(...TABLE_ALT_ROW);
      doc.rect(MARGIN, y, CONTENT_WIDTH, rowHeight, 'F');
      doc.setTextColor(...DARK);
      doc.setFont('helvetica', 'normal');
    } else {
      doc.setTextColor(...DARK);
      doc.setFont('helvetica', 'normal');
    }
    
    doc.setFontSize(8);
    x = MARGIN + 3;
    row.forEach((cell, i) => {
      if (i === 0) {
        doc.text(cell, x, y + 5.5);
      } else {
        doc.text(cell, x + colWidths[i] - 3, y + 5.5, { align: 'right' });
      }
      x += colWidths[i];
    });
    
    y += rowHeight;
  });

  // Bottom border
  doc.setDrawColor(200, 200, 200);
  doc.line(MARGIN, y, MARGIN + CONTENT_WIDTH, y);

  return y;
}

function drawSummaryRow(doc: jsPDF, y: number, label: string, value: string, highlight?: boolean) {
  if (highlight) {
    doc.setFillColor(...GREEN);
    doc.rect(MARGIN, y - 5, CONTENT_WIDTH, 10, 'F');
    doc.setTextColor(...WHITE);
    doc.setFont('helvetica', 'bold');
  } else {
    doc.setTextColor(...DARK);
    doc.setFont('helvetica', 'normal');
  }
  doc.setFontSize(10);
  doc.text(label, MARGIN + 5, y + 1);
  doc.text(value, MARGIN + CONTENT_WIDTH - 5, y + 1, { align: 'right' });
  return y + 12;
}

function addSectionTitle(doc: jsPDF, y: number, title: string): number {
  // Green accent bar
  doc.setFillColor(...GREEN);
  doc.rect(MARGIN, y, 3, 8, 'F');
  
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(...DARK);
  doc.text(title, MARGIN + 8, y + 7);
  
  return y + 16;
}

// ========== PAGE GENERATORS ==========

function pageCover(doc: jsPDF, params: BusinesscasePDFParams) {
  const { businesscase, project } = params;
  
  // Full green background top section
  doc.setFillColor(...GREEN);
  doc.rect(0, 0, PAGE_WIDTH, 130, 'F');
  
  // Logo text
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(28);
  doc.setTextColor(...WHITE);
  doc.text('GreenVision', MARGIN, 45);
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(12);
  doc.text('Businesscase Rapport', MARGIN, 58);
  
  // Businesscase name
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(24);
  doc.text(businesscase.name, MARGIN, 90);
  
  // Project info
  if (project) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(14);
    const projectLabel = project.project_number 
      ? `${project.project_number} — ${project.name}` 
      : project.name;
    doc.text(projectLabel, MARGIN, 108);
  }
  
  // Date
  doc.setFontSize(11);
  doc.text(formatDate(), MARGIN, 122);
  
  // Notes section below green
  if (businesscase.notes) {
    let y = 150;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(...DARK);
    doc.text('Notities', MARGIN, y);
    y += 8;
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(...GRAY);
    const noteLines = doc.splitTextToSize(businesscase.notes, CONTENT_WIDTH);
    doc.text(noteLines, MARGIN, y);
  }
  
  // Version info bottom
  doc.setFontSize(9);
  doc.setTextColor(...GRAY);
  doc.text(`Versie ${businesscase.version}`, MARGIN, PAGE_HEIGHT - 20);
  doc.text(`ROI modus: ${params.roiMode === 'bruto' ? 'Bruto' : 'Netto'}`, MARGIN, PAGE_HEIGHT - 12);
}

function pageTableOfContents(doc: jsPDF) {
  addHeader(doc, 'Inhoudsopgave');
  addFooter(doc, 2);
  
  let y = 50;
  
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.setTextColor(...DARK);
  doc.text('Inhoudsopgave', MARGIN, y);
  y += 20;
  
  const chapters = [
    { num: '1', title: 'Kosten & Investering', page: '3' },
    { num: '2', title: 'Subsidies & Belastingen', page: '4' },
    { num: '3', title: 'Financiële Samenvatting', page: '5' },
    { num: '4', title: 'Verwachte Inkomsten', page: '6' },
    { num: '5', title: 'Cashflow Analyse', page: '7' },
  ];
  
  chapters.forEach((ch) => {
    // Number circle
    doc.setFillColor(...GREEN);
    doc.circle(MARGIN + 5, y - 2, 5, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...WHITE);
    doc.text(ch.num, MARGIN + 5, y, { align: 'center' });
    
    // Title
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(13);
    doc.setTextColor(...DARK);
    doc.text(ch.title, MARGIN + 16, y);
    
    // Dotted line
    doc.setDrawColor(200, 200, 200);
    doc.setLineDashPattern([1, 2], 0);
    const titleWidth = doc.getTextWidth(ch.title);
    doc.line(MARGIN + 17 + titleWidth, y - 1, PAGE_WIDTH - MARGIN - 15, y - 1);
    doc.setLineDashPattern([], 0);
    
    // Page number
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...GREEN);
    doc.text(ch.page, PAGE_WIDTH - MARGIN, y, { align: 'right' });
    
    y += 18;
  });
}

function pageCosts(doc: jsPDF, params: BusinesscasePDFParams) {
  const { businesscase, extraCosts, calculations } = params;
  addHeader(doc, 'Kosten & Investering');
  addFooter(doc, 3);
  
  let y = 30;
  y = addSectionTitle(doc, y, '1. Kosten & Investering');
  y += 5;
  
  const costRows: string[][] = [
    ['Batterij aankoop', formatCurrency(businesscase.battery_purchase_cost)],
    ['Installatie', formatCurrency(businesscase.installation_cost)],
    ['Software setup', formatCurrency(businesscase.software_setup_cost)],
    ['Jaarlijks abonnement', formatCurrency(businesscase.annual_subscription)],
    ['Overige kosten', formatCurrency(businesscase.other_costs)],
  ];
  
  // Add extra costs
  extraCosts.forEach(ec => {
    costRows.push([ec.name, formatCurrency(ec.amount)]);
  });
  
  // Total row
  costRows.push(['Totaal Investering', formatCurrency(calculations.totalInvestment)]);
  
  const colWidths = [CONTENT_WIDTH * 0.6, CONTENT_WIDTH * 0.4];
  y = drawTable(doc, y, ['Kostenpost', 'Bedrag'], costRows, colWidths, { totalRow: true });
  
  // Info text
  y += 15;
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(9);
  doc.setTextColor(...GRAY);
  doc.text('Alle bedragen zijn exclusief BTW tenzij anders vermeld.', MARGIN, y);
}

function pageSubsidies(doc: jsPDF, params: BusinesscasePDFParams) {
  const { businesscase, calculations } = params;
  addHeader(doc, 'Subsidies & Belastingen');
  addFooter(doc, 4);
  
  let y = 30;
  y = addSectionTitle(doc, y, '2. Subsidies & Belastingen');
  y += 5;
  
  const subsidyRows: string[][] = [
    ['EIA (Energie Investerings Aftrek)', businesscase.eia_enabled ? 'Ja' : 'Nee', businesscase.eia_enabled ? formatCurrency(calculations.eiaValue) : '—'],
    ['Flex-E subsidie', businesscase.flex_e_enabled ? 'Ja' : 'Nee', businesscase.flex_e_enabled ? formatCurrency(businesscase.flex_e_value) : '—'],
    ['Overige subsidies', '—', formatCurrency(businesscase.other_subsidy)],
  ];
  
  const colWidths = [CONTENT_WIDTH * 0.45, CONTENT_WIDTH * 0.2, CONTENT_WIDTH * 0.35];
  y = drawTable(doc, y, ['Subsidie', 'Status', 'Waarde'], subsidyRows, colWidths);
  
  y += 15;
  
  // Tax rate
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...DARK);
  doc.text(`Belastingtarief: ${formatPercentage(businesscase.tax_rate)}`, MARGIN, y);
  y += 15;
  
  // Net investment summary
  y = addSectionTitle(doc, y, 'Netto Investering');
  y += 5;
  
  y = drawSummaryRow(doc, y, 'Totale Investering', formatCurrency(calculations.totalInvestment));
  y = drawSummaryRow(doc, y, 'EIA aftrek', `- ${formatCurrency(calculations.eiaValue)}`);
  
  const totalSubsidies = (businesscase.flex_e_enabled ? businesscase.flex_e_value : 0) + businesscase.other_subsidy;
  y = drawSummaryRow(doc, y, 'Overige subsidies', `- ${formatCurrency(totalSubsidies)}`);
  
  doc.setDrawColor(...GREEN);
  doc.setLineWidth(0.5);
  doc.line(MARGIN, y - 4, MARGIN + CONTENT_WIDTH, y - 4);
  
  y = drawSummaryRow(doc, y, 'Netto Investering', formatCurrency(calculations.netInvestment), true);
}

function pageFinancialSummary(doc: jsPDF, params: BusinesscasePDFParams) {
  const { calculations, roiMode } = params;
  addHeader(doc, 'Financiële Samenvatting');
  addFooter(doc, 5);
  
  let y = 30;
  y = addSectionTitle(doc, y, '3. Financiële Samenvatting');
  y += 10;
  
  // Key metrics in boxes
  const metrics = [
    { label: 'Totale Investering', value: formatCurrency(calculations.totalInvestment) },
    { label: 'EIA Waarde', value: formatCurrency(calculations.eiaValue) },
    { label: 'Netto Investering', value: formatCurrency(calculations.netInvestment) },
    { label: `ROI Jaar 1 (${roiMode})`, value: formatPercentage(calculations.roiYear1) },
    { label: 'ROI Best Case', value: formatPercentage(calculations.roiBestCase) },
    { label: 'ROI Worst Case', value: formatPercentage(calculations.roiWorstCase) },
    { label: 'Terugverdientijd', value: calculations.paybackPeriod ? `${calculations.paybackPeriod.toFixed(1)} jaar` : 'N.v.t.' },
    { label: 'Totale Cashflow', value: formatCurrency(calculations.totalCashflow) },
  ];
  
  const boxWidth = (CONTENT_WIDTH - 10) / 2;
  const boxHeight = 28;
  
  metrics.forEach((m, i) => {
    const col = i % 2;
    const row = Math.floor(i / 2);
    const bx = MARGIN + col * (boxWidth + 10);
    const by = y + row * (boxHeight + 8);
    
    // Box background
    doc.setFillColor(...LIGHT_GRAY);
    doc.roundedRect(bx, by, boxWidth, boxHeight, 3, 3, 'F');
    
    // Green left accent
    doc.setFillColor(...GREEN);
    doc.rect(bx, by, 3, boxHeight, 'F');
    
    // Label
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...GRAY);
    doc.text(m.label, bx + 8, by + 10);
    
    // Value
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(...DARK);
    doc.text(m.value, bx + 8, by + 22);
  });
  
  y += Math.ceil(metrics.length / 2) * (boxHeight + 8) + 15;
  
  // Risk indicator
  y = addSectionTitle(doc, y, 'Risico-indicatie');
  y += 5;
  
  // Tot 8 september 2026 oordeelde de PDF hier zelf, op uitsluitend het
  // rendement over jaar 1 en de terugverdientijd: `roi > 15 && payback < 5` gaf
  // "Laag risico". De kasstroom over de looptijd kwam er niet in voor. Dat is
  // dezelfde regel die op het scherm al is weggehaald, en hij was hier op twee
  // manieren mis.
  //
  // Aantoonbaar mis, vandaag, op een echt record: Core 1.4MW haalt 420.828 euro
  // omzet, houdt 242.447 euro over en verdient zich in 1 jaar terug, maar kreeg
  // "Hoog risico" omdat roiYear1 op de terugvalwaarde 0 stond.
  //
  // Latent mis: de acht cases met een negatieve kasstroom kregen "Hoog risico"
  // alleen doordat hun terugverdientijd null is en de eerste twee takken daarop
  // afketsen. Vul iemand die terugverdientijd in, dan drukt de PDF "Laag risico"
  // af terwijl het scherm ernaast "slecht" zegt — JWM staat op 30,26% bij
  // -10.377 euro. Eén oordeel op één plek maakt dat onmogelijk.
  const oordeel = beoordeelBusinesscase({
    roiYear1: calculations.roiYear1,
    totalCashflow: calculations.totalCashflow,
    paybackPeriod: calculations.paybackPeriod,
    roiMode,
    looptijdJaren: calculations.yearlyData?.length,
  });

  const riskColorPerNiveau = {
    goed: [34, 197, 94],
    aandacht: [234, 179, 8],
    slecht: [239, 68, 68],
    onbekend: [107, 114, 128],
  } as const;
  // Uit elkaar gehaald i.p.v. gespreid: een unie van tuples laat TypeScript niet
  // in een rest-parameter vallen (TS2556).
  const [riskR, riskG, riskB] = riskColorPerNiveau[oordeel.niveau];

  doc.setFillColor(riskR, riskG, riskB);
  doc.roundedRect(MARGIN, y, CONTENT_WIDTH, 14, 3, 3, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...WHITE);
  doc.text(oordeel.risicoLabel, PAGE_WIDTH / 2, y + 9.5, { align: 'center' });
  y += 20;

  // De reden staat eronder. Een gekleurde balk zonder uitleg dwingt de lezer de
  // getallen zelf naast elkaar te leggen; juist dat ging drie keer mis.
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...GRAY);
  doc.text(doc.splitTextToSize(oordeel.uitleg, CONTENT_WIDTH), MARGIN, y);
}

function pageRevenues(doc: jsPDF, params: BusinesscasePDFParams): number {
  const { revenues } = params;
  addHeader(doc, 'Verwachte Inkomsten');
  addFooter(doc, 6);
  
  let y = 30;
  y = addSectionTitle(doc, y, '4. Verwachte Inkomsten per Jaar');
  y += 5;
  
  if (revenues.length === 0) {
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(10);
    doc.setTextColor(...GRAY);
    doc.text('Geen inkomstendata beschikbaar.', MARGIN, y);
    return y;
  }
  
  // Revenue labels (short)
  const revenueFields: { key: keyof BusinesscaseRevenue; label: string }[] = [
    { key: 'grid_connection_savings', label: 'Netaansluiting' },
    { key: 'grid_purchase_cost_savings', label: 'Inkoop besparing' },
    { key: 'dayahead_incomes', label: 'Dayahead' },
    { key: 'hbe_incomes', label: "HBE's" },
    { key: 'energy_tax_savings', label: 'Energiebelasting' },
    { key: 'endex_cost_savings', label: 'Endex' },
    { key: 'imbalance_income', label: 'Onbalans ink.' },
    { key: 'imbalance_discount', label: 'Onbalans kort.' },
    { key: 'ems_costs', label: 'EMS kosten' },
    { key: 'total_opex', label: 'Totaal OPEX' },
    { key: 'generator_cost_savings', label: 'Generator' },
    { key: 'extra_sde', label: 'Extra SDE' },
    { key: 'total_solar_revenues', label: 'Solar' },
  ];
  
  const sortedRevenues = [...revenues].sort((a, b) => a.year - b.year);
  
  // Split into chunks of 5 years per table to fit the page
  const chunkSize = 5;
  const chunks: BusinesscaseRevenue[][] = [];
  for (let i = 0; i < sortedRevenues.length; i += chunkSize) {
    chunks.push(sortedRevenues.slice(i, i + chunkSize));
  }
  
  chunks.forEach((chunk, ci) => {
    if (ci > 0) {
      doc.addPage();
      addHeader(doc, 'Verwachte Inkomsten (vervolg)');
      addFooter(doc, 6 + ci);
      y = 30;
    }
    
    const yearHeaders = ['Inkomstenstroom', ...chunk.map(r => `Jaar ${r.year}`)];
    const yearColWidth = (CONTENT_WIDTH - 55) / chunk.length;
    const colWidths = [55, ...chunk.map(() => yearColWidth)];
    
    const rows: string[][] = revenueFields.map(field => {
      return [
        field.label,
        ...chunk.map(r => formatCurrency(Number(r[field.key]) || 0)),
      ];
    });
    
    y = drawTable(doc, y, yearHeaders, rows, colWidths);
    y += 5;
  });
  
  return y;
}

function pageCashflow(doc: jsPDF, params: BusinesscasePDFParams) {
  const { calculations } = params;
  addHeader(doc, 'Cashflow Analyse');
  
  // Dynamic page number based on revenue pages
  const pageNum = doc.getNumberOfPages();
  addFooter(doc, pageNum);
  
  let y = 30;
  y = addSectionTitle(doc, y, '5. Cashflow Analyse');
  y += 5;
  
  const yearlyData = calculations.yearlyData;
  if (!yearlyData || yearlyData.length === 0) {
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(10);
    doc.setTextColor(...GRAY);
    doc.text('Geen cashflow data beschikbaar.', MARGIN, y);
    return;
  }
  
  const headers = ['Jaar', 'Omzet', 'OPEX', 'Base CF', 'Best CF', 'Worst CF', 'Cum. CF'];
  const colWidth = CONTENT_WIDTH / headers.length;
  const colWidths = headers.map(() => colWidth);
  
  const rows = yearlyData.map(yd => [
    `${yd.year}`,
    formatCurrency(yd.revenue),
    formatCurrency(yd.opex),
    formatCurrency(yd.cashflowBase),
    formatCurrency(yd.cashflowBest),
    formatCurrency(yd.cashflowWorst),
    formatCurrency(yd.cumulativeCashflow),
  ]);
  
  y = drawTable(doc, y, headers, rows, colWidths);
  
  y += 15;
  
  // Payback summary
  if (calculations.paybackPeriod) {
    doc.setFillColor(240, 253, 244);
    doc.roundedRect(MARGIN, y, CONTENT_WIDTH, 20, 3, 3, 'F');
    doc.setFillColor(...GREEN);
    doc.rect(MARGIN, y, 3, 20, 'F');
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...GREEN);
    doc.text(`Terugverdientijd: ${calculations.paybackPeriod.toFixed(1)} jaar`, MARGIN + 10, y + 13);
  } else {
    doc.setFillColor(254, 242, 242);
    doc.roundedRect(MARGIN, y, CONTENT_WIDTH, 20, 3, 3, 'F');
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(239, 68, 68);
    doc.text('Investering wordt niet terugverdiend binnen de looptijd', MARGIN + 10, y + 13);
  }
}

// ========== MAIN EXPORT ==========

export function generateBusinesscasePDF(params: BusinesscasePDFParams) {
  const doc = new jsPDF('p', 'mm', 'a4');
  
  // Page 1: Cover
  pageCover(doc, params);
  
  // Page 2: Table of Contents
  doc.addPage();
  pageTableOfContents(doc);
  
  // Page 3: Costs
  doc.addPage();
  pageCosts(doc, params);
  
  // Page 4: Subsidies
  doc.addPage();
  pageSubsidies(doc, params);
  
  // Page 5: Financial Summary
  doc.addPage();
  pageFinancialSummary(doc, params);
  
  // Page 6+: Revenues (may add extra pages)
  doc.addPage();
  pageRevenues(doc, params);
  
  // Last page: Cashflow
  doc.addPage();
  pageCashflow(doc, params);
  
  // Download
  const filename = `${params.businesscase.name.replace(/[^a-zA-Z0-9]/g, '_')}_businesscase.pdf`;
  doc.save(filename);
}
